import { CountUp } from '@ck/ui';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '@ck/sdk';

/** Loads data once (and again when deps change), with loading and error states. */
export function useApi<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    setError(null);
    fn().then((d) => live.current && setData(d)).catch((e) => live.current && setError(e instanceof Error ? e.message : 'Something went wrong.'));
    return () => { live.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, reload, setData };
}

/** Wraps an action with busy/error/success state for buttons and forms. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const run = useCallback(async <T,>(fn: () => Promise<T>, success?: string): Promise<T | undefined> => {
    setBusy(true); setError(null); setDone(null);
    try { const r = await fn(); if (success) setDone(success); return r; }
    catch (e) { setError(e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong.'); return undefined; }
    finally { setBusy(false); }
  }, []);
  return { busy, error, done, run, setError, setDone };
}

export function PageHead({ kicker, title, em, lead, children }: { kicker?: string; title: string; em?: string; lead?: ReactNode; children?: ReactNode }) {
  const heading = `${title}${em ? ` ${em}` : ''}`.replace(/\.$/, '');
  return (
    <header className="ph">
      <div className="ph-t">
        {kicker && <span className="kicker">{kicker}</span>}
        <h1>{heading}</h1>
        {lead && <p className="lead">{lead}</p>}
      </div>
      {children}
    </header>
  );
}

export const Loading = ({ label = 'Loading' }: { label?: string }) => <div className="loading" aria-busy="true">{label}…</div>;
export const Failed = ({ error, retry }: { error: string; retry?: () => void }) => (
  <div className="msg err" role="alert">{error} {retry && <button className="link" onClick={retry}>Try again</button>}</div>
);
export const Empty = ({ title, children }: { title: string; children?: ReactNode }) => (
  <div className="empty"><b>{title}</b>{children && <span>{children}</span>}</div>
);
export const Note = ({ kind = 'ok', children }: { kind?: 'ok' | 'err' | 'info'; children: ReactNode }) => <p className={`msg ${kind}`} role={kind === 'err' ? 'alert' : 'status'}>{children}</p>;

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return <div className="stat"><span className="mono">{label}</span><b><CountUp value={value} /></b>{sub && <small>{sub}</small>}</div>;
}

export function Meter({ label, value, sub, danger }: { label: string; value: number | null; sub?: string; danger?: boolean }) {
  if (value === null || value === undefined) return null;
  const tone = danger ? (value >= 80 ? 'bad' : value >= 50 ? 'warn' : 'ok') : 'good';
  return (
    <div className={`meter ${tone}`}>
      <div className="meter-h"><span>{label}</span><b>{Math.round(value)}%</b></div>
      <div className="meter-bar" role="progressbar" aria-label={label} aria-valuenow={Math.round(value)} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${Math.min(100, Math.max(0, value))}%` }} /></div>
      {sub && <small>{sub}</small>}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);
  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-h"><h2>{title}</h2><button className="x" aria-label="Close" onClick={onClose}>×</button></div>
        {children}
      </div>
    </div>
  );
}

export function Copy({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" className="btn ghost sm" onClick={async () => {
      try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1600); } catch { /* clipboard blocked */ }
    }}>{ok ? 'Copied' : label}</button>
  );
}

export const STATUS_LABEL: Record<string, string> = {
  on_hold: 'On hold', chargeback: 'Chargeback', waiting: 'Waiting', in_progress: 'In progress',
  pending_payment: 'Awaiting payment', awaiting_review: 'Payment being checked', paid: 'Paid', rejected: 'Rejected', cancelled: 'Cancelled',
  requested: 'Requested', approved: 'Approved', active: 'In progress', failed: 'Breached', funded: 'Funded',
  not_started: 'Not started', pending: 'Under review', funding_review: 'Passed — funding review', revoked: 'Withdrawn',
};
export const Badge = ({ status }: { status: string }) => <span className={`badge s-${status}`}>{STATUS_LABEL[status] ?? status}</span>;
